import { z } from 'zod';
import { AJUSTE_SCOPE_TIPO, type AjusteScopeTipo } from '../cobros/types';

const moneyRegex = /^\d+(\.\d{1,2})?$/;
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?$/;

export { AJUSTE_SCOPE_TIPO };
export type { AjusteScopeTipo };

/**
 * Spec (Domain: billing-adjustments): cobro_excepcion is restricted to
 * invoice-scope or period-scope. Either idCobro or periodoInicio (with
 * idCliente) MUST be set. Backend rejects unscoped submissions (422).
 */
export const CreateExcepcionDtoSchema = z
  .object({
    idCliente: z.coerce.number().int(),
    idCobro: z.coerce.number().int().positive().optional().nullable(),
    periodoInicio: z.string().regex(isoDateRegex).optional().nullable(),
    periodoFin: z.string().regex(isoDateRegex).optional().nullable(),
    monto: z.string().regex(moneyRegex),
    motivo: z.string().min(1),
    /**
     * Caller-owned idempotency key. Reusing the same key retries this exact
     * adjustment; omitting it intentionally creates a distinct adjustment.
     * The backend must never derive this key from the adjustment payload.
     */
    operacionKey: z.string().min(1).max(255).optional(),
  })
  .refine((v) => v.idCobro !== undefined || v.periodoInicio !== undefined, {
    message: 'cobro_excepcion requires idCobro or periodoInicio (scope check)',
  });

export type CreateExcepcionDto = z.infer<typeof CreateExcepcionDtoSchema>;

export const UpdateExcepcionDtoSchema = z.object({
  idCobro: z.coerce.number().int().positive().optional().nullable(),
  periodoInicio: z.string().regex(isoDateRegex).optional().nullable(),
  periodoFin: z.string().regex(isoDateRegex).optional().nullable(),
  monto: z.string().regex(moneyRegex).optional(),
  motivo: z.string().optional(),
});

export type UpdateExcepcionDto = z.infer<typeof UpdateExcepcionDtoSchema>;

export const QueryExcepcionesDtoSchema = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  idCliente: z.coerce.number().int().optional(),
  idUsuario: z.string().uuid().optional(),
  fechaDesde: z.string().regex(isoDateRegex).optional(),
  fechaHasta: z.string().regex(isoDateRegex).optional(),
});

export type QueryExcepcionesDto = z.infer<typeof QueryExcepcionesDtoSchema>;

/** Immutable adjustment metadata exposed via the response. */
export const ExcepcionResponseDtoSchema = z.object({
  id: z.number(),
  idCliente: z.number(),
  idCobro: z.number().nullable(),
  periodoInicio: z.string().nullable(),
  periodoFin: z.string().nullable(),
  monto: z.string(),
  motivo: z.string(),
  scope: z.enum([AJUSTE_SCOPE_TIPO.INVOICE, AJUSTE_SCOPE_TIPO.PERIOD]),
  actor: z.string(),
  fechaCreacion: z.string().regex(isoDateTimeRegex),
});

export type ExcepcionResponseDto = z.infer<typeof ExcepcionResponseDtoSchema>;
